// VOY-485 (rebuild, post-audit) — Profile page.
//
// One /profile route, two tabs (Public profile / Settings), pill toggle
// top-right, constant header above both panes. No /settings route.
//
// Public profile tab — who you are as a traveler + activity:
//   Identity · Traveler stats · Style+interests · Keywords · Traveler DNA
//   Badges · Gaming · Places visited · Plan+Wallet · My trips
//   Recent searches · Recent purchases
//
// Settings tab — account administration:
//   Account · Currency & locale · Travel documents · Travel preferences
//   Search behavior · Loyalty programs · Traveler Notes · Travel Companions
//   Privacy · Connected accounts (full-width)
//
// Plumbing preserved from the live profile:
//   - useAuth(), updateProfile(), getCurrentSubscription(),
//     getUserQuizResults(), getPreferences()/savePreferences()
// New plumbing:
//   - getTravelerStats(), getPlacesVisited(), getRecentOrders()
// Mounted (VOY-465): PrivacyControls inside PrivacySection.
// Built fresh (VOY-459 / VOY-460): TravelerNotesSection + TravelCompanionsSection
//   against the shipped backends + lib helpers.
//
// Stubs (intentional, per ticket): "saved w/ Voy" stat → em-dash + PENDING.
// Placeholders: Badges, Gaming · Level.

import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth'
import {
  getUserProfile,
  getUserQuizResults,
  getPreferences,
  savePreferences,
  getTravelerStats,
  getPlacesVisited,
  type TravelerStats,
  type PlaceVisited,
} from '@/lib/profile'
import { getCurrentSubscription } from '@/lib/stripe'
import type { Profile, TravelPreferences } from '@/types/profile'
import type { UserSubscription } from '@/types/subscription'
import type { QuizResultForProfile } from '@/components/profile/helpers/quiz-helpers'

import { IdentityCard } from '@/components/profile/v485/IdentityCard'
import { TravelerStatsSection } from '@/components/profile/v485/TravelerStatsSection'
import { StyleInterestsSection } from '@/components/profile/v485/StyleInterestsSection'
import { KeywordsSection } from '@/components/profile/v485/KeywordsSection'
import { TravelerDnaSection } from '@/components/profile/v485/TravelerDnaSection'
import { BadgesSection } from '@/components/profile/v485/BadgesSection'
import { GamingSection } from '@/components/profile/v485/GamingSection'
import { PlacesVisitedSection } from '@/components/profile/v485/PlacesVisitedSection'
import { PlanWalletSection } from '@/components/profile/v485/PlanWalletSection'
import { MyTripsSection } from '@/components/profile/v485/MyTripsSection'
import { RecentSearchesSection } from '@/components/profile/v485/RecentSearchesSection'
import { RecentPurchasesSection } from '@/components/profile/v485/RecentPurchasesSection'

import { AccountSection } from '@/components/profile/v485/AccountSection'
import { CurrencyLocaleSection } from '@/components/profile/v485/CurrencyLocaleSection'
import { TravelDocumentsSection } from '@/components/profile/v485/TravelDocumentsSection'
import { TravelPreferencesCategoricalSection } from '@/components/profile/v485/TravelPreferencesCategoricalSection'
import { TravelBehaviorTogglesSection } from '@/components/profile/v485/TravelBehaviorTogglesSection'
import { LoyaltyProgramsSection } from '@/components/profile/v485/LoyaltyProgramsSection'
import { TravelerNotesSection } from '@/components/profile/v485/TravelerNotesSection'
import { TravelCompanionsSection } from '@/components/profile/v485/TravelCompanionsSection'
import { PrivacySection } from '@/components/profile/v485/PrivacySection'
import { ConnectedAccountsSection } from '@/components/profile/v485/ConnectedAccountsSection'

import { QuizModal } from '@/components/profile/quiz/QuizModal'

type TabKey = 'public' | 'settings'

export default function ProfilePage(): ReactNode {
  const { user } = useAuth()
  const [tab, setTab] = useState<TabKey>('public')
  const [quizOpen, setQuizOpen] = useState(false)

  const [profile, setProfile] = useState<Profile | null>(null)
  const [subscription, setSubscription] = useState<UserSubscription | null>(null)
  const [quiz, setQuiz] = useState<QuizResultForProfile | null>(null)
  const [preferences, setPreferences] = useState<TravelPreferences>({})
  const [stats, setStats] = useState<TravelerStats | null>(null)
  const [places, setPlaces] = useState<PlaceVisited[]>([])
  const [loading, setLoading] = useState(true)
  // Tracks a fatal load error so the user gets a real error state with a
  // retry button instead of a perpetual spinner. Per-slice failures are
  // still swallowed via the inline catches; this state surfaces only the
  // catastrophic case (e.g. unauthenticated, network down).
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      return
    }
    void loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  async function loadAll() {
    if (!user?.id) return
    setLoading(true)
    setLoadError(null)
    try {
      const [
        profileData,
        subData,
        quizData,
        preferencesData,
        statsData,
        placesData,
      ] = await Promise.all([
        getUserProfile(),
        getCurrentSubscription().catch((err) => {
          console.error('[ProfilePage] subscription load failed:', err)
          return null
        }),
        getUserQuizResults(user.id).catch((err) => {
          console.error('[ProfilePage] quiz load failed:', err)
          return null
        }),
        getPreferences(user.id, 'travel').catch((err) => {
          console.error('[ProfilePage] preferences load failed:', err)
          return {}
        }),
        getTravelerStats(user.id).catch((err) => {
          console.error('[ProfilePage] stats load failed:', err)
          return null
        }),
        getPlacesVisited(user.id).catch((err) => {
          console.error('[ProfilePage] places load failed:', err)
          return [] as PlaceVisited[]
        }),
      ])
      // If the profile itself is null after the auth-gated fetch, treat
      // it as a fatal error rather than rendering a half-built page with
      // no identity. Sub-slices already degrade gracefully above.
      if (!profileData) {
        setLoadError("We couldn't load your profile. Sign in again or try once more.")
        setProfile(null)
        return
      }
      setProfile(profileData)
      setSubscription(subData)
      setQuiz((quizData as QuizResultForProfile | null) ?? null)
      setPreferences((preferencesData as TravelPreferences | null) ?? {})
      setStats(statsData)
      setPlaces(placesData)
    } catch (err) {
      console.error('[ProfilePage] loadAll failed:', err)
      setLoadError(err instanceof Error ? err.message : 'Failed to load profile.')
    } finally {
      setLoading(false)
    }
  }

  function reload() {
    void loadAll()
  }

  async function handlePreferencesSave(next: TravelPreferences) {
    if (!user?.id) return
    await savePreferences(user.id, 'travel', next as Record<string, unknown>)
    setPreferences(next)
  }

  function onAfterDelete() {
    // VOY-465's PrivacyControls fires this when the 7-day grace is
    // scheduled. Reload so the parent picks up `status='pending_deletion'`.
    reload()
  }

  if (loading) {
    return (
      <div className="voy-v6">
        <div className="page">
          <div className="profile-loading">Loading your profile…</div>
        </div>
      </div>
    )
  }

  if (loadError || !profile) {
    return (
      <div className="voy-v6">
        <div className="page">
          <div className="profile-loading" style={{ flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0 }}>{loadError ?? "We couldn't load your profile."}</p>
            <button
              type="button"
              className="btn-primary"
              style={{ padding: '7px 14px', fontSize: 12 }}
              onClick={() => void loadAll()}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="voy-v6">
      <div className="page">
        <header className="ph">
          <div className="eyebrow"><span className="dot" />ACCOUNT</div>
          <div className="row between" style={{ alignItems: 'flex-end' }}>
            <div>
              <h1>Profile</h1>
              <p className="lede">Manage your account, public traveler profile, and preferences.</p>
            </div>
            <div className="tabs" role="tablist" aria-label="Profile sections">
              <button
                id="tab-public-button"
                role="tab"
                aria-selected={tab === 'public'}
                aria-controls="tab-public"
                className={tab === 'public' ? 'active' : undefined}
                onClick={() => setTab('public')}
              >
                Public profile
              </button>
              <button
                id="tab-settings-button"
                role="tab"
                aria-selected={tab === 'settings'}
                aria-controls="tab-settings"
                className={tab === 'settings' ? 'active' : undefined}
                onClick={() => setTab('settings')}
              >
                Settings
              </button>
            </div>
          </div>
        </header>

        {/* ============================== Public profile tab ===== */}
        {/* Identity + declared inputs that define who you are as a
            traveler. TravelPreferences (categorical), Loyalty programs,
            and Traveler Notes were moved here from Settings — they are
            user-declared inputs that feed the agent, not account
            administration. */}
        <section
          id="tab-public"
          role="tabpanel"
          className="tab-pane"
          data-active={tab === 'public'}
          aria-hidden={tab !== 'public'}
          aria-labelledby="tab-public-button"
        >
          {/* Row 1: Identity + Traveler stats */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <IdentityCard
              profile={profile}
              subscription={subscription}
              onProfileChanged={reload}
            />
            <TravelerStatsSection stats={stats} />
          </div>

          {/* Row 2: Style+Interests + Keywords (declared identity inputs) */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <StyleInterestsSection preferences={preferences} onSave={handlePreferencesSave} />
            <KeywordsSection userId={profile.id} />
          </div>

          {/* Row 3: Travel preferences (6-field categorical) + Traveler DNA */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <TravelPreferencesCategoricalSection preferences={preferences} onSave={handlePreferencesSave} />
            <TravelerDnaSection quiz={quiz} onTakeQuiz={() => setQuizOpen(true)} />
          </div>

          {/* Row 4: Loyalty programs + Plan & Wallet */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <LoyaltyProgramsSection userId={profile.id} />
            <PlanWalletSection profile={profile} subscription={subscription} onRefresh={reload} />
          </div>

          {/* Row 5: Traveler Notes (full-width — variable height) */}
          <div style={{ marginBottom: 18 }}>
            <TravelerNotesSection userId={profile.id} />
          </div>

          {/* Row 6: Badges + Gaming (placeholders) */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <BadgesSection />
            <GamingSection />
          </div>

          {/* Row 7: Places visited (full-width) */}
          <div style={{ marginBottom: 18 }}>
            <PlacesVisitedSection places={places} />
          </div>

          {/* Row 8: My trips (full-width) */}
          <div style={{ marginBottom: 18 }}>
            <MyTripsSection userId={profile.id} />
          </div>

          {/* Row 9: Recent searches + Recent purchases */}
          <div className="grid-2">
            <RecentSearchesSection userId={profile.id} />
            <RecentPurchasesSection userId={profile.id} />
          </div>
        </section>

        {/* ============================== Settings tab =========== */}
        {/* Account administration only. Identity inputs (preferences,
            loyalty, notes) moved to Public profile. Settings keeps the
            configuration surfaces: account fields, currency/locale,
            travel documents, search-behavior toggles, privacy, and
            connected accounts. Travel Companions stays here because
            companions are account-managed records the owner curates
            rather than personal-identity declarations. */}
        <section
          id="tab-settings"
          role="tabpanel"
          className="tab-pane"
          data-active={tab === 'settings'}
          aria-hidden={tab !== 'settings'}
          aria-labelledby="tab-settings-button"
        >
          {/* Row 1: Account + Currency & locale */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <AccountSection profile={profile} onProfileChanged={reload} />
            <CurrencyLocaleSection userId={profile.id} />
          </div>

          {/* Row 2: Travel documents (full-width) */}
          <div style={{ marginBottom: 18 }}>
            <TravelDocumentsSection />
          </div>

          {/* Row 3: Search behavior + Privacy */}
          <div className="grid-2" style={{ marginBottom: 18 }}>
            <TravelBehaviorTogglesSection userId={profile.id} />
            <PrivacySection userId={profile.id} onAfterDelete={onAfterDelete} />
          </div>

          {/* Row 4: Travel Companions (full-width) */}
          <div style={{ marginBottom: 18 }}>
            <TravelCompanionsSection userId={profile.id} />
          </div>

          {/* Row 5: Connected accounts (full-width per design) */}
          <ConnectedAccountsSection userId={profile.id} />
        </section>

        <footer className="app-foot">
          <div className="row">
            <span>© 2026 Voy</span>
          </div>
          <div className="row" style={{ fontFamily: 'var(--v6-font-mono)', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            <span style={{ color: 'var(--v6-pos)' }}>●</span> LIVE · ALL SYSTEMS GO
          </div>
        </footer>

        <QuizModal
          isOpen={quizOpen}
          onOpenChange={setQuizOpen}
          onComplete={() => {
            // Refresh profile so the new quiz result lights up the DNA spectrum.
            reload()
          }}
        />
      </div>
    </div>
  )
}
